<template>
  <div>
    <input
        ref="excel-upload-input"
        accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        class="excel-upload-input"
        type="file"
        @change="handleClick"
    />
    <div
        :class="['drop', {'drop--active': isDragging}]"
        @dragenter="handleDragenter"
        @dragleave="handleDragleave"
        @dragover="handleDragover"
        @drop="handleDrop"
    >
      <div class="drop__inner">
        <v-progress-circular
            v-if="loading"
            class="mb-4"
            color="primary"
            indeterminate
            size="42"
            width="4"
        />
        <v-icon v-else color="primary" size="40">mdi-file-excel-outline</v-icon>
        <div class="drop__title">
          {{ loading ? "Reading spreadsheet..." : "Drop Your Excel/CSV File Here" }}
        </div>
        <div class="drop__subtitle">
          {{ loading ? "Validating sheets and headers before import." : "Use the downloaded sample format. Supported files: `.xlsx`, `.xls`, or `.csv` up to 1 MB." }}
        </div>
        <v-btn :disabled="loading" color="primary" depressed rounded type="button" @click="handleUpload">
          Select File
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import XLSX from "xlsx";

export default {
  props: {
    beforeUpload: Function, // eslint-disable-line
    onSuccess: Function, // eslint-disable-line
    parseAllSheets: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      loading: false,
      excelData: {
        fileName: '',
        header: null,
        results: null,
        sheets: null,
      },
    };
  },
  methods: {
    notifyError(message) {
      if (this.$toast?.error) {
        this.$toast.error(message);
      } else {
        console.error(message);
      }
    },
    generateData({header, results, fileName, sheets}) {
      this.excelData.header = header;
      this.excelData.results = results;
      this.excelData.fileName = fileName;
      this.excelData.sheets = sheets || null;
      this.onSuccess && this.onSuccess(this.excelData);
    },
    handleDrop(e) {
      e.stopPropagation();
      e.preventDefault();
      this.isDragging = false;
      if (this.loading) return;
      const files = e.dataTransfer.files;
      this.processFileList(files);
    },
    handleDragenter(e) {
      e.stopPropagation();
      e.preventDefault();
      this.isDragging = true;
    },
    handleDragleave(e) {
      e.stopPropagation();
      e.preventDefault();
      this.isDragging = false;
    },
    handleDragover(e) {
      e.stopPropagation();
      e.preventDefault();
      this.isDragging = true;
      e.dataTransfer.dropEffect = "copy";
    },
    handleUpload() {
      this.$refs["excel-upload-input"].click();
    },
    handleClick(e) {
      const files = e.target.files;
      this.processFileList(files);
    },
    processFileList(files) {
      if (!files || files.length !== 1) {
        this.notifyError("Only support uploading one file!");
        return false;
      }
      const rawFile = files[0];
      if (!rawFile) return false;
      if (!this.isExcel(rawFile)) {
        this.notifyError("Only supports upload .xlsx, .xls, .csv suffix files");
        this.$refs["excel-upload-input"].value = null;
        return false;
      }

      this.upload(rawFile);
      return true;
    },
    resolvePrimarySheetName(sheetNames) {
      if (!Array.isArray(sheetNames) || !sheetNames.length) {
        return null;
      }

      return (
        sheetNames.find((sheetName) => /template|data/i.test(sheetName)) ||
        sheetNames.find((sheetName) => !/instructions?/i.test(sheetName)) ||
        sheetNames[0]
      );
    },
    isScientificNotation(value) {
      return /^[+-]?(?:\d+\.?\d*|\.\d+)[eE][+-]?\d+$/.test(String(value || "").trim());
    },
    stringifyRawNumber(value) {
      if (typeof value !== "number" || !Number.isFinite(value)) return null;
      if (Number.isInteger(value)) {
        return value.toFixed(0);
      }
      return String(value);
    },
    restoreScientificNumericCells(formattedRows, rawRows) {
      if (!Array.isArray(formattedRows) || !Array.isArray(rawRows)) {
        return formattedRows;
      }

      return formattedRows.map((row, rowIndex) => {
        const rawRow = rawRows[rowIndex] || {};
        const nextRow = {...row};
        Object.keys(nextRow).forEach((key) => {
          if (!this.isScientificNotation(nextRow[key])) return;
          const rawNumber = this.stringifyRawNumber(rawRow[key]);
          if (rawNumber !== null) {
            nextRow[key] = rawNumber;
          }
        });
        return nextRow;
      });
    },
    upload(rawFile) {
      this.$refs["excel-upload-input"].value = null; // fix can't select the same excel
      if (!this.beforeUpload) {
        this.readerData(rawFile);
        return;
      }
      const before = this.beforeUpload(rawFile);
      if (before) {
        this.readerData(rawFile);
      }
    },
    readerData(rawFile) {
      this.loading = true;
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target.result;
            const workbook = XLSX.read(data, {
              type: "array",
              cellDates: true,
              dateNF: "yyyy-mm-dd",
            });
            const fileName = rawFile.name;
            const sheets = {};

            workbook.SheetNames.forEach((sheetName) => {
              const worksheet = workbook.Sheets[sheetName];
              const header = this.getHeaderRow(worksheet);
              const results = XLSX.utils.sheet_to_json(worksheet, {
                defval: "",
                raw: false,
                blankrows: false,
              });
              const rawResults = XLSX.utils.sheet_to_json(worksheet, {
                defval: "",
                raw: true,
                blankrows: false,
              });
              sheets[sheetName] = {
                header,
                results: this.restoreScientificNumericCells(results, rawResults),
              };
            });

            if (!workbook.SheetNames.length) {
              this.notifyError("The selected file has no readable sheet.");
              return;
            }

            const primarySheetName = this.resolvePrimarySheetName(workbook.SheetNames);
            const primarySheet = sheets[primarySheetName] || {header: [], results: []};
            if (!primarySheet.results?.length) {
              this.notifyError("The selected spreadsheet is empty. Please add at least one data row.");
              return;
            }
            this.generateData({
              header: primarySheet.header,
              results: primarySheet.results,
              fileName,
              sheets,
            });
          } catch (error) {
            console.error(error);
            this.notifyError("Unable to open the selected Excel/CSV file. Please check the file format.");
          } finally {
            this.loading = false;
            resolve();
          }
        };
        reader.onerror = () => {
          this.loading = false;
          this.notifyError("Unable to read the selected file. Please try again.");
          this.loading = false;
          resolve();
        };
        reader.readAsArrayBuffer(rawFile);
      });
    },
    getHeaderRow(sheet) {
      const headers = [];
      if (!sheet || !sheet["!ref"]) {
        return headers;
      }
      const range = XLSX.utils.decode_range(sheet["!ref"]);
      let C;
      const R = range.s.r;
      /* start in the first row */
      for (C = range.s.c; C <= range.e.c; ++C) {
        /* walk every column in the range */
        const cell = sheet[XLSX.utils.encode_cell({c: C, r: R})];
        /* find the cell in the first row */
        let hdr = "UNKNOWN " + C; // <-- replace with your desired default
        if (cell && cell.t) hdr = XLSX.utils.format_cell(cell);
        headers.push(hdr);
      }
      return headers;
    },
    isExcel(file) {
      const fileName = String(file?.name || "").toLowerCase();
      return /\.(xlsx|xls|csv)$/.test(fileName);
    },
  },
};
</script>

<style scoped>
.excel-upload-input {
  display: none;
  z-index: -9999;
}

.drop {
  border: 2px dashed #94a3b8;
  width: 100%;
  min-height: 220px;
  margin: 0 auto;
  border-radius: 16px;
  text-align: center;
  color: #64748b;
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%);
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.drop--active {
  border-color: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 18px 40px rgba(37, 99, 235, 0.12);
}

.drop__inner {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 14px;
  padding: 24px;
}

.drop__title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.drop__subtitle {
  font-size: 14px;
  color: #475569;
}
</style>
